/**
 * Editorial API: backlog, drafts, workflow, review, scheduling, import/export.
 * Type-agnostic — every puzzle-type specific behaviour is reached through the registry.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";
import type { Capabilities } from "@/core/puzzles/api";

export type PuzzleStatus =
  "draft" | "submitted" | "in_review" | "approved" | "scheduled" | "published" | "archived";

export const PUZZLE_STATUSES: PuzzleStatus[] = [
  "draft",
  "submitted",
  "in_review",
  "approved",
  "scheduled",
  "published",
  "archived",
];

export interface PuzzleSummaryInfo {
  description: string;
  thumbnail: Record<string, unknown>;
}

export interface BacklogPuzzle {
  slug: string;
  title: string;
  author_name: string;
  difficulty: number;
  status: PuzzleStatus;
  tags: string[];
  puzzle_type: string;
  credit_author: string;
  credit_url: string;
  created_at: string;
  updated_at: string;
  summary: PuzzleSummaryInfo | null;
  capabilities: Capabilities;
  available_transitions: PuzzleStatus[];
  starts_at: string | null;
  version_number: number;
  open_comments: number;
}

export interface PuzzleTypeInfo {
  key: string;
  version: number;
  capabilities: Capabilities;
  check_strategy: string;
  labels: Record<string, string>;
  empty_definition: unknown;
  import_formats: string[];
  export_formats: string[];
  /** Option fields the type wants the import dialog to show. Rendered as declared. */
  import_options_schema: ImportOptionSpec[];
}

export interface PuzzleVersionDetail {
  id: number;
  number: number;
  definition: unknown;
  type_version: number;
  is_frozen: boolean;
  has_solution: boolean;
  created_at: string;
}

export interface ReviewComment {
  id: number;
  author_name: string;
  body: string;
  resolved: boolean;
  anchor: Record<string, unknown> | null;
  created_at: string;
}

export interface PublicationInfo {
  starts_at: string;
  published_at: string | null;
}

export interface BacklogDetail {
  puzzle: BacklogPuzzle;
  version: PuzzleVersionDetail | null;
  publication: PublicationInfo | null;
  comments: ReviewComment[];
  /** Whether this user may open the stored solution. The detail never carries it. */
  can_read_solution: boolean;
}

export interface StoredSolution {
  version_number: number;
  has_solution: boolean;
  solution: unknown;
}

/** An author-facing validation message. Only `severity: "error"` blocks a transition. */
export interface DefinitionErrorItem {
  path: string;
  code: string;
  params: Record<string, unknown>;
  severity: "error" | "warning";
}

export interface DraftSaveResult {
  version: PuzzleVersionDetail;
  errors: DefinitionErrorItem[];
}

export interface UnsupportedItem {
  source: string;
  kind: string;
  data: unknown;
  reason?: string | null;
}

export interface ImportResultView {
  definition: unknown;
  solution: unknown;
  partial: boolean;
  unsupported: UnsupportedItem[];
  errors: DefinitionErrorItem[];
}

export interface ExportResultView {
  format: string;
  /** The definition for `native`; the type's own payload for any other format. */
  data: unknown;
  unsupported: UnsupportedItem[];
}

interface Page<T> {
  count: number;
  results: T[];
}

export interface BacklogFilters {
  status: PuzzleStatus[];
  type: string;
  mine: boolean;
  q: string;
}

export const EMPTY_FILTERS: BacklogFilters = { status: [], type: "", mine: false, q: "" };

export function backlogQueryString(filters: BacklogFilters): string {
  const params = new URLSearchParams();
  for (const status of filters.status) params.append("status", status);
  if (filters.type) params.set("type", filters.type);
  if (filters.mine) params.set("mine", "true");
  if (filters.q) params.set("q", filters.q);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/**
 * Per-status counts, but only when the whole backlog fits on the page we already fetched.
 * Returns null otherwise rather than showing a number that would be wrong.
 */
export function statusCounts(page: Page<BacklogPuzzle> | undefined): Record<string, number> | null {
  if (!page || page.count > page.results.length) return null;
  const counts: Record<string, number> = {};
  for (const puzzle of page.results) counts[puzzle.status] = (counts[puzzle.status] ?? 0) + 1;
  return counts;
}

export function useBacklog(filters: BacklogFilters) {
  return useQuery({
    queryKey: ["backlog", "list", filters],
    queryFn: () => api<Page<BacklogPuzzle>>(`/api/backlog/${backlogQueryString(filters)}`),
  });
}

export function usePuzzleTypes() {
  return useQuery({
    queryKey: ["backlog", "types"],
    queryFn: () => api<PuzzleTypeInfo[]>("/api/backlog/types/"),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreatePuzzle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      puzzle_type: string;
      difficulty?: number;
      tags?: string[];
    }) => api<BacklogPuzzle>("/api/backlog/", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backlog", "list"] }),
  });
}

export function useBacklogDetail(slug: string) {
  return useQuery({
    queryKey: ["backlog", "detail", slug],
    queryFn: () => api<BacklogDetail>(`/api/backlog/${slug}/`),
    enabled: Boolean(slug),
  });
}

/**
 * Reads a stored solution back for review (ADR 0015). Deliberately manual: the solution is
 * fetched only when someone asks for it, never as a side effect of opening the page.
 */
export function useStoredSolution(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ["backlog", "solution", slug],
    queryFn: () => api<StoredSolution>(`/api/backlog/${slug}/solution/`),
    enabled: enabled && Boolean(slug),
    staleTime: 0,
    gcTime: 0,
  });
}

function useDetailInvalidation(slug: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["backlog", "detail", slug] });
    void qc.invalidateQueries({ queryKey: ["backlog", "list"] });
  };
}

/** The editable half of a puzzle's metadata; everything else on `Puzzle` is read-only here. */
export interface PuzzleMetadataPatch {
  title?: string;
  difficulty?: number;
  tags?: string[];
  credit_author?: string;
  credit_url?: string;
}

export function useUpdateMetadata(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: (body: PuzzleMetadataPatch) =>
      api<BacklogPuzzle>(`/api/backlog/${slug}/`, { method: "PATCH", json: body }),
    onSuccess: invalidate,
  });
}

export function useSaveDraft(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: (body: { definition: unknown; solution?: unknown }) =>
      api<DraftSaveResult>(`/api/backlog/${slug}/draft/`, { method: "PUT", json: body }),
    onSuccess: invalidate,
  });
}

export function usePuzzleTransition(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: (body: { target: PuzzleStatus; starts_at?: string }) =>
      api<BacklogPuzzle>(`/api/backlog/${slug}/transition/`, { method: "POST", json: body }),
    onSuccess: invalidate,
  });
}

export function useSchedule(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: (body: { starts_at: string }) =>
      api<PublicationInfo>(`/api/backlog/${slug}/schedule/`, { method: "PUT", json: body }),
    onSuccess: invalidate,
  });
}

export function useAddComment(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: (body: { body: string; anchor?: Record<string, unknown> }) =>
      api<ReviewComment>(`/api/backlog/${slug}/comments/`, { method: "POST", json: body }),
    onSuccess: invalidate,
  });
}

export function useUpdateComment(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: ({ id, resolved }: { id: number; resolved: boolean }) =>
      api<ReviewComment>(`/api/backlog/${slug}/comments/${id}/`, {
        method: "PATCH",
        json: { resolved },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteComment(slug: string) {
  const invalidate = useDetailInvalidation(slug);
  return useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/backlog/${slug}/comments/${id}/`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export type Localized = Record<string, string | undefined>;

export interface ImportOptionSpec {
  id: string;
  kind: "boolean" | "integer" | "string" | "choice";
  label?: Localized;
  help?: Localized;
  default?: unknown;
  choices?: { value: string; label?: Localized }[];
}

export interface ImportBody {
  payload: string;
  /** Which type should read the payload: the core does not know any format itself. */
  puzzle_type: string;
  options?: Record<string, unknown>;
  fetch?: boolean;
}

export function useImportPayload() {
  return useMutation({
    mutationFn: (body: ImportBody) =>
      api<ImportResultView>("/api/backlog/import/", { method: "POST", json: body }),
  });
}

export function useExportPuzzle(slug: string) {
  return useMutation({
    mutationFn: (format: string) => api<ExportResultView>(`/api/backlog/${slug}/export/${format}/`),
  });
}
